/**
 * Login / post-auth orchestration
 *
 * Pipeline after a successful authenticate:
 *   1. Load tenants
 *   2. Pick tenant (modal if needed) → switch tenant
 *   3. If retailer: load workspaces (modal if needed)
 *   4. Load user info + permissions → redirect (or refresh in place)
 *
 * Same helpers are reused for in-session tenant/workspace switches (usedFor === "update")
 * and for retailer-selection refresh (no redirect).
 *
 * Behavior (delays, cookies, return shapes, toasts) must stay identical to the previous
 * requestConfig implementation — this file is a readability extract only.
 */

import { toast } from "react-toastify";
import { updateStateData } from "../Store/actions";
import store from "../Store/store";
import {
  clearRetailerSelectionData,
  createCookie,
  deleteCookie,
  markAnnouncementAutoOpenPending,
  sendApiRequest,
  setLoadingStatus,
} from "./requestConfig";
import { COOKIE_MAX_AGE_MS, setSessionEndsAt } from "./authTokenUtils";

// userManagement is loaded lazily inside helpers below to avoid a circular import:
// loginFlow → userManagement → requestConfig → loginFlow

// Intentional waits from the original flow — do not remove without verifying backend timing.
const POST_AUTH_DELAY_MS = 1000;
const POST_CONTEXT_SWITCH_RELOAD_MS = 2000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Lazy access to userManagement exports (keeps module init cycle-free). */
const userManagement = () => require("./userManagement");

// ---------------------------------------------------------------------------
// Tenant option helpers
// ---------------------------------------------------------------------------

/** Normalize API access_type strings ("Brand Access" → "brand"). */
const normalizeAccessType = (accessType) => {
  const normalizedAccessType =
    typeof accessType !== "undefined" && accessType !== null ? accessType.toString().trim().toLowerCase() : "";
  return normalizedAccessType === "brand access" ? "brand" : normalizedAccessType;
};

const isBrandAccess = (tenantAccess) => {
  return normalizeAccessType(tenantAccess.access_type) === "brand";
};

const tenantDisplayName = (tenantAccess) => {
  return isBrandAccess(tenantAccess)
    ? "Brand Access"
    : typeof tenantAccess.access_name !== "undefined"
      ? tenantAccess.access_name
      : "";
};

/** Stable select value: accessType-tenantId-companyId-index */
const tenantOptionValue = (tenantAccess, index) => {
  return [
    normalizeAccessType(tenantAccess.access_type),
    tenantAccess.tenant_id,
    tenantAccess.company_id,
    index,
  ].join("-");
};

/**
 * Turn raw /tenants response into dropdown options.
 * Brand access is listed first; other tenants are A–Z by label.
 */
const normalizeTenantOptions = (tenantItems) => {
  const tenantOptions = Array.isArray(tenantItems)
    ? tenantItems.map((tenantAccess, index) => {
        const accessType = normalizeAccessType(tenantAccess.access_type);
        return {
          label: tenantDisplayName({ ...tenantAccess, access_type: accessType }),
          value: tenantOptionValue({ ...tenantAccess, access_type: accessType }, index),
          access_name: tenantAccess.access_name,
          access_type: accessType,
          tenant_id: tenantAccess.tenant_id,
          company_id: tenantAccess.company_id,
        };
      })
    : [];

  const brandAccessOption = tenantOptions.find((tenantAccess) => tenantAccess.access_type === "brand");
  const tenantAccessOptions = tenantOptions
    .filter((tenantAccess) => tenantAccess.access_type !== "brand")
    .sort((currentOption, nextOption) => currentOption.label.localeCompare(nextOption.label));
  const orderedTenantOptions =
    typeof brandAccessOption !== "undefined" ? [brandAccessOption, ...tenantAccessOptions] : tenantAccessOptions;
  const uniqueTenantIds = [...new Set(tenantAccessOptions.map((tenantAccess) => tenantAccess.tenant_id))];
  const mainAccessType =
    tenantAccessOptions.length > 0
      ? tenantAccessOptions[0].access_type
      : orderedTenantOptions.length > 0
        ? orderedTenantOptions[0].access_type
        : "";

  return {
    options: orderedTenantOptions,
    tenant_options: tenantAccessOptions,
    has_brand_access: typeof brandAccessOption !== "undefined",
    main_access_type: mainAccessType,
    unique_tenant_count: uniqueTenantIds.length,
  };
};

/** Persist distinct access types from tenant options (feeds workspace "Brand Access" option). */
const saveAccessTypesFromTenantOptions = (tenantOptions) => {
  const accessTypeItems = [];
  tenantOptions.forEach((tenantOption) => {
    if (
      tenantOption.access_type !== "" &&
      accessTypeItems.some((accessTypeItem) => accessTypeItem.access_type === tenantOption.access_type) === false
    ) {
      accessTypeItems.push({ access_type: tenantOption.access_type });
    }
  });

  const accessRespObj = {
    has_multiple_access: accessTypeItems.length > 1,
    access_types: accessTypeItems,
  };

  store.dispatch(updateStateData(accessRespObj));
  createCookie("access_types", JSON.stringify(accessRespObj));
};

/** Show tenant modal when there are multiple tenants, or one tenant plus brand access. */
const tenantSelectionRequired = (tenantDetails) => {
  return (
    tenantDetails.unique_tenant_count > 1 ||
    (tenantDetails.unique_tenant_count === 1 && tenantDetails.has_brand_access === true)
  );
};

/** Show workspace modal when multiple workspaces, brand flag, or multiple access types. */
const workspaceSelectionRequired = (workSpaceItems, hasBrandAccess, accessTypeDetails) => {
  return (
    workSpaceItems.length > 1 ||
    hasBrandAccess === true ||
    accessTypeDetails.has_multiple_access === true
  );
};

// ---------------------------------------------------------------------------
// Cookie readers used by login + BottomNav
// ---------------------------------------------------------------------------

export const checkTenantOptionsFromCookies = () => {
  if (typeof document.cookie.split("; ").find((row) => row.startsWith("tenant_options")) !== "undefined") {
    return {
      success: true,
      tenant_options: JSON.parse(
        document.cookie
          .split("; ")
          .find((row) => row.startsWith("tenant_options"))
          .split("=")[1]
      ),
    };
  }
  return {
    success: false,
    tenant_options: [],
  };
};

export const checkSelectedTenantFromCookies = () => {
  if (typeof document.cookie.split("; ").find((row) => row.startsWith("selected_tenant")) !== "undefined") {
    return {
      success: true,
      selected_tenant: JSON.parse(
        document.cookie
          .split("; ")
          .find((row) => row.startsWith("selected_tenant"))
          .split("=")[1]
      ),
    };
  }
  return {
    success: false,
    selected_tenant: {},
  };
};

// ---------------------------------------------------------------------------
// Step: tenants
// ---------------------------------------------------------------------------

export const checkTenants = async () => {
  // Tenant switcher options. Not scoped by the retailer view.
  const accessTypes = await sendApiRequest("tenants", {}, "GET", { skipRetailerViewFilter: true });

  if (typeof accessTypes.success !== "undefined" && accessTypes.success === true) {
    const tenantDetails = normalizeTenantOptions(accessTypes.response);

    if (tenantDetails.options.length === 0) {
      return {
        success: false,
        message: "No access found for this user. Please contact support.",
      };
    }

    store.dispatch(
      updateStateData({
        tenant_options: tenantDetails.options,
        show_tenant_list: false,
      })
    );
    createCookie("tenant_options", JSON.stringify(tenantDetails.options));
    saveAccessTypesFromTenantOptions(tenantDetails.options);

    return {
      success: true,
      ...tenantDetails,
    };
  }

  return {
    success: false,
  };
};

/**
 * After credentials succeed: load tenants, open modal if needed, otherwise auto-select.
 */
const handleTenantLoginFlow = async (navigate) => {
  const tenantDetails = await checkTenants();

  if (typeof tenantDetails.success === "undefined" || tenantDetails.success === false) {
    deleteCookie("authToken");
    deleteCookie("tenant_options");
    deleteCookie("selected_tenant");
    deleteCookie("access_types");
    clearRetailerSelectionData();
    store.dispatch(
      updateStateData({
        show_tenant_list: false,
        show_workspace_list: false,
      })
    );
    setLoadingStatus(false);
    toast(typeof tenantDetails.message !== "undefined" ? tenantDetails.message : "Sorry, something went wrong.", {
      type: "error",
    });
    return;
  }

  if (tenantSelectionRequired(tenantDetails) === true) {
    store.dispatch(
      updateStateData({
        show_tenant_list: true,
        tenant_options: tenantDetails.options,
      })
    );
    setLoadingStatus(false);
    return;
  }

  // Single clear choice — switch without showing the modal
  const tenantOption =
    tenantDetails.tenant_options.length > 0 ? tenantDetails.tenant_options[0] : tenantDetails.options[0];
  if (typeof tenantOption !== "undefined") {
    selectTenantAccess(tenantOption, navigate);
  }
};

// ---------------------------------------------------------------------------
// Step: after tenant is chosen
// ---------------------------------------------------------------------------

/**
 * Remember selected tenant, then:
 * - retailer → workspace list / modal
 * - brand (or other) → load user details directly
 */
const completeLoginByAccessType = async (tenantOption, navigate, usedFor) => {
  store.dispatch(
    updateStateData({
      selected_tenant: tenantOption,
      show_tenant_list: false,
    })
  );
  createCookie("selected_tenant", JSON.stringify(tenantOption));

  const accessType = normalizeAccessType(tenantOption.access_type);

  if (accessType === "retailer") {
    return fetchWorkSpaceList(navigate, false, usedFor);
  }

  // users/info expects the normalized value ("brand"), not the API label ("Brand Access")
  return checkAuthorizedUserDetails(navigate, usedFor, accessType);
};

export const selectTenantAccess = async (tenantOption, navigate, usedFor) => {
  if (typeof tenantOption === "undefined") {
    toast("Sorry, something went wrong.", { type: "error" });
    return {
      success: false,
    };
  }

  /* Brand access is listed next to the tenants but is not a tenant itself, so it carries no
     switchable tenant_id. Skip tenants/switch and load the brand user details directly,
     the same way the workspace selector handles it (see switchToBrandAccess). */
  if (isBrandAccess(tenantOption) === true) {
    clearRetailerSelectionData();
    return completeLoginByAccessType(tenantOption, navigate, usedFor);
  }

  if (typeof tenantOption.tenant_id === "undefined" || tenantOption.tenant_id === null) {
    toast("Sorry, something went wrong.", { type: "error" });
    return {
      success: false,
    };
  }

  clearRetailerSelectionData();
  const switchTenant = await sendApiRequest("tenants/switch/" + tenantOption.tenant_id, {}, "POST");

  if (typeof switchTenant.success !== "undefined" && switchTenant.success === true) {
    if (usedFor === "update") {
      toast("Tenant successfully changed!", { type: "success", position: "top-right" });
    }
    // Return value matters for BottomNav: may include show_workspace_list
    return completeLoginByAccessType(tenantOption, navigate, usedFor);
  }

  toast("Sorry, something went wrong.", { type: "error" });
  return {
    success: false,
  };
};

// ---------------------------------------------------------------------------
// Step: workspaces (retailer access)
// ---------------------------------------------------------------------------

const fetchWorkSpaceOptions = async () => {
  const fetchWorkSpaceDetail = await sendApiRequest("workspaces", {}, "GET", { skipRetailerViewFilter: true });

  if (typeof fetchWorkSpaceDetail.success !== "undefined" && fetchWorkSpaceDetail.success === true) {
    return {
      success: true,
      workspace_options: fetchWorkSpaceDetail.response,
    };
  }

  return {
    success: false,
  };
};

const saveWorkSpaceOptions = (workSpaceItems, showWorkspaceList) => {
  store.dispatch(
    updateStateData({
      show_workspace_list: showWorkspaceList,
      workspace_options: workSpaceItems,
    })
  );
  createCookie("workspace_options", JSON.stringify(workSpaceItems));
};

/**
 * Load workspaces for the current tenant.
 * If only one workspace (and no multi-access), continue straight into user-info.
 * Otherwise open the workspace modal (show_workspace_list).
 */
export const fetchWorkSpaceList = async (navigate, hasBrandAccess = false, usedFor) => {
  const workspaceListInfo = await fetchWorkSpaceOptions();

  if (typeof workspaceListInfo.success !== "undefined" && workspaceListInfo.success === true) {
    const workSpaceItems = workspaceListInfo.workspace_options;
    const checkAccesstypeValues = userManagement().checkAccessTypeFromCookie();
    const accessTypeDetails = typeof checkAccesstypeValues !== "undefined" ? checkAccesstypeValues.access_types : {};
    const showWorkspaceList = workspaceSelectionRequired(workSpaceItems, hasBrandAccess, accessTypeDetails);

    saveWorkSpaceOptions(workSpaceItems, showWorkspaceList);

    if (showWorkspaceList === false) {
      return checkAuthorizedUserDetails(navigate, usedFor, "retailer");
    }

    return {
      success: true,
      workspace_options: workSpaceItems,
      show_workspace_list: showWorkspaceList,
    };
  }

  return {
    success: false,
  };
};

// ---------------------------------------------------------------------------
// Step: user info + redirect
// ---------------------------------------------------------------------------

/**
 * Resolve which access_type to send to users/info when the caller did not pass one.
 * A selected Brand Access wins outright, otherwise non-brand entries from the access_types
 * cookie take over from selected_tenant.
 */
export const findUsersAccesstype = () => {
  const checkAccesstypeValues = userManagement().checkAccessTypeFromCookie();
  const selectedTenantDetails = checkSelectedTenantFromCookies();
  const selectedTenant = selectedTenantDetails.selected_tenant;
  let userAccessTypeName = "retailer";

  if (typeof selectedTenant.access_type !== "undefined" && selectedTenant.access_type !== "") {
    userAccessTypeName = selectedTenant.access_type;
  }

  /* An explicit Brand Access selection wins. The fallback below deliberately drops brand for
     users with several access types, which would otherwise silently undo that choice. */
  if (isBrandAccess(selectedTenant) === true) {
    return "brand";
  }

  if (
    typeof checkAccesstypeValues.access_types !== "undefined" &&
    typeof checkAccesstypeValues.access_types.access_types !== "undefined" &&
    checkAccesstypeValues.access_types.access_types.length !== 0
  ) {
    let userAccessType =
      checkAccesstypeValues.access_types.access_types.length > 1
        ? checkAccesstypeValues.access_types.access_types.filter(
            (accessName) => normalizeAccessType(accessName.access_type) !== "brand"
          )
        : checkAccesstypeValues.access_types.access_types;

    if (userAccessType.length !== 0) {
      userAccessTypeName = normalizeAccessType(userAccessType[0].access_type);
    }
  }
  return userAccessTypeName;
};

/** Map users/info response → cookie/Redux userDetail shape (unchanged fields). */
const persistAuthorizedUser = (apiResponse) => {
  const userDetail = {
    user_email: apiResponse.email,
    user_name: apiResponse.username,
    user_role: apiResponse.roles[0],
    assigned_user_roles: apiResponse.roles,
    user_role_info: apiResponse.role_info
      .sort((next, prev) => {
        return next.role_name.localeCompare(prev.role_name);
      })
      .map((role) => {
        return { role_name: role.role_name, description: role.description };
      }),
    force_reset_password: false,
    current_workspace: apiResponse.assigned_company,
    user_type: apiResponse.user_type,
    root_company_id: apiResponse.root_company_id,
    assigned_company: apiResponse.assigned_company,
    deactivated_sections: apiResponse.deactivated_sections,
    loop_visual_limit: apiResponse.loop_visual_limit,
    loop_visual_max_duration: apiResponse.loop_visual_max_duration,
  };

  // Cookies survive refresh; Redux alone would be wiped on reload.
  createCookie("userPermissions", JSON.stringify(apiResponse.permissions));
  createCookie("userRole", JSON.stringify(userDetail.user_role));
  createCookie("userDetail", JSON.stringify(userDetail));
  createCookie("current_workspace", JSON.stringify(userDetail.current_workspace));

  store.dispatch(updateStateData(userDetail));

  return userDetail;
};

/**
 * Pick landing page from permissions, then navigate and/or reload.
 * usedFor === "update" (in-session switch) always reloads after a short delay.
 *
 * Important: reload/navigate timers are fire-and-forget so this function can
 * return { success, user_detail } immediately — BottomNav/TenantSelection await
 * that return (e.g. show_workspace_list) before the reload runs.
 */
const redirectAfterLogin = async (navigate, usedFor, userDetail) => {
  const landingPage = await userManagement().allowedNavOptions({ pathname: "/" });
  let redirectTo = "/cap-overview";

  if (typeof landingPage.navItems !== "undefined" && landingPage.navItems.length !== 0) {
    if (landingPage.navItems[0].nested_list === true) {
      redirectTo = landingPage.navItems[0].nested_items[0].path;
    } else {
      redirectTo = landingPage.navItems[0].path;
    }
  }

  if (userDetail.force_reset_password === true) {
    redirectTo = "/accounts/reset-password";
  }

  if (window.location.pathname === redirectTo) {
    // Already on the target page — reload so workspace/tenant context refreshes.
    setTimeout(() => {
      window.location.reload();
    }, POST_CONTEXT_SWITCH_RELOAD_MS);
  } else if (usedFor === "update") {
    // In-session context switch: navigate, then full reload to reset Redux.
    setTimeout(() => {
      navigate(redirectTo);
      window.location.reload();
    }, POST_CONTEXT_SWITCH_RELOAD_MS);
  } else {
    // Fresh login onto a different path — client navigate only.
    navigate(redirectTo);
  }
};

/**
 * Fetch users/info, persist session cookies, optionally redirect.
 *
 * config.redirectOnSuccess / redirectOnFailure default to true.
 * refreshAuthorizedUserDetails turns both off (retailer selection, etc.).
 */
export const checkAuthorizedUserDetails = async (navigate, usedFor, accessSelection, config = {}) => {
  const redirectOnSuccess = typeof config.redirectOnSuccess !== "undefined" ? config.redirectOnSuccess : true;
  const redirectOnFailure = typeof config.redirectOnFailure !== "undefined" ? config.redirectOnFailure : true;
  const accessType = typeof accessSelection !== "undefined" ? accessSelection : findUsersAccesstype();
  const endPoint = "users/info?access_type=" + accessType;
  const fetchUserDetail = await sendApiRequest(endPoint, {}, "GET");

  if (typeof fetchUserDetail.success !== "undefined" && fetchUserDetail.success === true) {
    const userDetail = persistAuthorizedUser(fetchUserDetail.response);

    if (redirectOnSuccess === false) {
      return {
        success: true,
        user_detail: userDetail,
      };
    }

    await redirectAfterLogin(navigate, usedFor, userDetail);

    return {
      success: true,
      user_detail: userDetail,
    };
  }

  if (redirectOnFailure === true) {
    window.location.href = "/";
  }
  return {
    success: false,
  };
};

/** Refresh user cookies/Redux without navigating (e.g. retailer selection). */
export const refreshAuthorizedUserDetails = async (accessSelection) => {
  return checkAuthorizedUserDetails(undefined, undefined, accessSelection, {
    redirectOnSuccess: false,
    redirectOnFailure: false,
  });
};

// ---------------------------------------------------------------------------
// Entry: username / password login
// ---------------------------------------------------------------------------

/**
 * POST /authenticate → save authToken → wait → tenant/workspace/user pipeline.
 * loaderControl=true on authenticate keeps the global loader on until this flow turns it off.
 */
export const authorizeCredentials = async (userCredentials, navigate) => {
  const authenticateUser = await sendApiRequest("authenticate", userCredentials, "POST", true);

  if (typeof authenticateUser.success !== "undefined" && authenticateUser.success === true) {
    createCookie("authToken", authenticateUser.response.access_token);
    setSessionEndsAt(Date.now() + COOKIE_MAX_AGE_MS);
    // Inbox reads this on its first mount and opens once. A reload clears the flag.
    markAnnouncementAutoOpenPending();
    await delay(POST_AUTH_DELAY_MS);
    await handleTenantLoginFlow(navigate);
    return;
  }

  setLoadingStatus(false);
  toast("Please check your username and password and try again.", { type: "error" });
};
